/**
 * Pure helpers for in-page section navigation (no path aliases, unit tested).
 *
 * Why this exists: the SPA has `<base href="/">`, so a plain
 * `<a href="#section">` resolves to `/#section` and the router leaves the
 * current page (logged-in users land on Drive). In-page navs must scroll
 * the scroll container instead of navigating.
 */

export interface SectionRect {
  id: string;
  /** Section top relative to the scroll container's visible top (px). */
  top: number;
}

/**
 * Active section = the last one whose top has passed the offset line.
 * At the bottom of the container the last section wins; before the
 * first section the first one wins.
 */
export function pickActiveSection(
  sections: SectionRect[],
  offset: number,
  atBottom = false,
): string | null {
  if (!sections.length) return null;
  if (atBottom) return sections[sections.length - 1].id;
  let active = sections[0].id;
  for (const section of sections) {
    if (section.top - offset <= 1) active = section.id;
    else break;
  }
  return active;
}

/** scrollTop that puts `elementTop` (relative to the visible top) at `offset`. */
export function scrollTopFor(
  currentScrollTop: number,
  elementTop: number,
  offset: number,
): number {
  return Math.max(0, Math.round(currentScrollTop + elementTop - offset));
}

/** Same-document hash link ("#id") that must not go through the router. */
export function inPageHashTarget(rawHref: string | null | undefined): string | null {
  if (!rawHref || !rawHref.startsWith('#') || rawHref.length < 2) return null;
  try {
    return decodeURIComponent(rawHref.slice(1));
  } catch {
    return rawHref.slice(1);
  }
}
